import { Grid, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { styles } from './product-display-quickview-styles';
import { ReactComponent as MinusIcon } from 'src/common/media/minus_circle.svg';
import { ReactComponent as TrashIcon } from 'src/common/media/trash_circle.svg';
import { ReactComponent as CartIcon } from 'src/common/media/cart_small.svg';
import { ReactComponent as ChevronIcon } from 'src/common/media/chevron.svg';
import { Pagination } from 'src/pagination/pagination';
import { AppState } from 'src/common/redux/configure-store';
import { useTranslation } from 'react-i18next';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { cancelItem, cancelLine } from 'src/home-page/business-logic/cancel-line-or-item';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

const QuickView: React.FC<QuickViewProps> = ({
  toggleQuickView,
  isQuickviewVisible,
  isLeftHanded,
}): JSX.Element => {
  const classes = styles();

  const handlePageChange = (updatedPage: number): void => {
    if (updatedPage > currentPage) {
      setCurrentPage(currentPage + 1);
    } else {
      setCurrentPage(currentPage - 1);
    }
  };

  const ejItems = useSelector<AppState, IElectronicJournalRecord[]>(
    state => state.itemList.ejItems,
  );
  let products: IElectronicJournalRecord[] = [];
  products = ejItems
    .filter(item => !item.Voided)
    .filter(item => item.Type === EJRecordTypes.Product);
  const numItems = products.length;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(0);
  const pageSize = 8;

  useEffect(() => {
    setCurrentPage(lastPage);
  }, [numItems, lastPage]);

  useEffect(() => {
    if (numItems > 0) {
      const lastPage = Math.ceil(numItems / pageSize);
      if (lastPage < currentPage) {
        setCurrentPage(lastPage);
      }
      setLastPage(lastPage);
    }
  }, [numItems, currentPage, pageSize]);

  return (
    <Grid
      container
      data-testid='quickview-body'
      direction='column'
      className={clsx(classes.quickView, {
        [classes.leftHanded]: isLeftHanded,
        [classes.rightHanded]: !isLeftHanded,
      })}
    >
      <QuickViewToggleButton
        toggleQuickView={toggleQuickView}
        isQuickviewVisible={isQuickviewVisible}
      />
      <QuickViewItemList
        products={products}
        currentPage={currentPage}
        pageSize={pageSize}
        toggleQuickView={toggleQuickView}
      />
      <Grid container justifyContent='center' className={classes.paginationContainer}>
        <Pagination
          currentPage={currentPage}
          totalPages={lastPage}
          updateCurrentPageHandler={handlePageChange}
          testIdPrefix={'quickview'}
        />
      </Grid>
    </Grid>
  );
};

const QuickViewLabel: React.FC<QuickViewLabelProps> = ({ label }): JSX.Element => {
  const classes = styles();
  return (
    <Grid container className={classes.itemLabel}>
      <div data-testid='quickView-item-name' className={classes.itemLabelText}>
        <Typography variant={'body2'} style={{ textAlign: 'inherit' }}>
          {label}
        </Typography>
      </div>
    </Grid>
  );
};

const QuickViewQuantity: React.FC<QuickViewQuantityProps> = ({ item }): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const unitsLabel = item.UnitOfMeasureCode ? t(`UOM_${item.UnitOfMeasureCode}`) : '';
  return (
    <Grid item className={classes.itemQuantity}>
      <div data-testid='quickView-item-qty' className={classes.itemQuantityText}>
        <Typography variant={'body2'} style={{ textAlign: 'inherit' }}>
          {item.UnitOfMeasureCode === 'EA' ? item.Quantity : `${item.Quantity} ${unitsLabel}`}
        </Typography>
      </div>
    </Grid>
  );
};

const QuickViewButton: React.FC<QuickViewLineButtonProps> = ({ item }): JSX.Element => {
  const classes = styles();
  const quantity = item.Quantity ?? 0;
  const isWeighted = item.UnitOfMeasureCode !== 'EA';
  return (
    <Grid container className={classes.quickViewButtonContainer}>
      {quantity > 1 && !isWeighted ? (
        <MinusIcon
          className={classes.itemButton}
          data-testid='quickview-minus-icon'
          onClick={() => {
            cancelItem(item);
          }}
        />
      ) : (
        <TrashIcon
          className={classes.itemButton}
          data-testid='quickview-trash-icon'
          onClick={async () => {
            await cancelLine(item);
          }}
        />
      )}
    </Grid>
  );
};

const QuickViewItem: React.FC<QuickViewItemProps> = ({
  item,
  lineNum,
  numItems,
  toggleQuickView,
}: QuickViewItemProps): JSX.Element => {
  const classes = styles();
  return (
    <Grid
      container
      data-testid={`quickview-line-${lineNum}`}
      direction={'row'}
      className={classes.itemLine}
    >
      <QuickViewLabel label={item.Name} />
      <QuickViewQuantity item={item} />
      <QuickViewButton item={item} numItems={numItems} toggleQuickView={toggleQuickView} />
    </Grid>
  );
};

const QuickViewItemList: React.FC<QuickViewItemListProps> = ({
  products,
  currentPage,
  pageSize,
  toggleQuickView,
}): JSX.Element => {
  const classes = styles();
  const productPage = products?.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  return (
    <Grid item className={classes.itemList}>
      <Grid item className={classes.innerItemList}>
        {productPage.map((item, index) => {
          return (
            <QuickViewItem
              lineNum={index}
              key={`quickview-line-${index}`}
              item={item}
              numItems={products.length}
              toggleQuickView={toggleQuickView}
            />
          );
        })}
      </Grid>
    </Grid>
  );
};

export const QuickViewToggleButton: React.FC<QuickViewToggleButtonProps> = ({
  toggleQuickView,
  isQuickviewVisible,
}): JSX.Element => {
  const ejItems = useSelector<AppState, IElectronicJournalRecord[]>(
    state => state.itemList.ejItems,
  );
  let products: IElectronicJournalRecord[] = [];
  products = ejItems
    .filter(item => !item.Voided)
    .filter(item => item.Type === EJRecordTypes.Product);
  const numItems = products.length;

  return (
    <ToggleButtonComponent
      toggleQuickView={toggleQuickView}
      isQuickviewVisible={isQuickviewVisible}
      numItems={numItems}
    />
  );
};

const ToggleButtonComponent: React.FC<ToggleButtonComponentProps> = ({
  toggleQuickView,
  isQuickviewVisible,
  disabled,
  numItems,
}): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  return (
    <BaseButton
      data-testid='quickview-toggle'
      className={classes.quickViewToggleButton}
      onClick={toggleQuickView}
      disabled={disabled}
    >
      <Grid container direction={'row'} className={classes.quickViewToggleButtonLabel}>
        <Grid item>
          <CartIcon className={classes.cartIcon} />
        </Grid>
        <Grid item data-testid='quickview-itemsAdded-label' className={classes.itemsAddedLabel}>
          <Typography variant={'body2'}>{`${t('ItemsAdded')} (${numItems})`}</Typography>
        </Grid>
        <Grid item>
          {isQuickviewVisible ? (
            <ChevronIcon className={classes.chevronIcon} />
          ) : (
            <ChevronIcon className={classes.chevronIcon} transform='rotate(180)' />
          )}
        </Grid>
      </Grid>
    </BaseButton>
  );
};

interface QuickViewProps {
  toggleQuickView: () => void;
  isQuickviewVisible: boolean;
  isLeftHanded: boolean;
}

interface QuickViewLabelProps {
  label?: string;
}
interface QuickViewItemProps {
  item: IElectronicJournalRecord;
  lineNum: number;
  numItems: number;
  toggleQuickView: () => void;
}

interface QuickViewItemListProps {
  products: IElectronicJournalRecord[];
  currentPage: number;
  pageSize: number;
  toggleQuickView: () => void;
}
interface QuickViewQuantityProps {
  item: IElectronicJournalRecord;
}
interface QuickViewLineButtonProps {
  item: IElectronicJournalRecord;
  numItems: number;
  toggleQuickView: () => void;
}

interface QuickViewToggleButtonProps {
  toggleQuickView: () => void;
  isQuickviewVisible?: boolean;
}

interface ToggleButtonComponentProps {
  toggleQuickView: () => void;
  isQuickviewVisible?: boolean;
  disabled?: boolean;
  numItems: number;
}

export default React.memo(QuickView);
